/* @jsxImportSource react */
'use client';

import {
  type AriaAttributes,
  type ComponentPropsWithoutRef,
  type FocusEventHandler,
  forwardRef,
  useMemo,
} from 'react';

import { Input as BaseInput } from '@base-ui/react/input';
import { NumberField } from '@base-ui/react/number-field';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import {
  type AccessibleNameProps,
  hasAccessibleName,
  useAccessibleNameWarning,
} from '../../utils/accessible-name';
import { cn } from '../../utils/cn';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/** Input size definitions mapping names to their Tailwind classes. */
export const ATOMS_INPUT_VARIANTS = {
  size: {
    xs: {
      classes: 'gap-1 rounded-xl px-2.5 py-2 text-body-md',
      description: 'Extra small input for compact UIs',
    },
    sm: {
      classes: 'h-14 gap-1 rounded-2xl px-3 text-body-md',
      description: 'Small input for secondary fields',
    },
    base: {
      classes: 'h-16 gap-1.5 rounded-2xl px-4 text-body-lg',
      description: 'Default input size',
    },
    lg: {
      classes: 'h-20 gap-2 rounded-3xl px-5 text-body-xl',
      description: 'Large input for prominent fields',
    },
  },
} as const;

export const ATOMS_INPUT_DEFAULT_VARIANTS = {
  size: 'base',
} as const;

export const ATOMS_INPUT_STYLING = {
  dimensions: {
    xs: { height: 36, paddingX: 10, fontSize: 16, borderRadius: 12, width: 160 },
    sm: { height: 56, paddingX: 12, fontSize: 16, borderRadius: 16, width: 200 },
    base: {
      height: 64,
      paddingX: 16,
      fontSize: 18,
      borderRadius: 16,
      width: 280,
    },
    lg: { height: 80, paddingX: 20, fontSize: 20, borderRadius: 24, width: 320 },
  },
  baseTokens: {
    background: 'color-secondary',
    text: 'text-color-surface',
    placeholder: 'text-color-muted',
    ring: 'color-border',
  },
  stateTokens: {
    focus: { ring: 'color-active' },
    error: { ring: 'color-error' },
    disabled: {
      ring: 'color-border-inactive',
      background: 'color-inactive-secondary',
      text: 'text-color-muted',
    },
  },
} as const;

/** @public */
export type AtomsInputSize = keyof typeof ATOMS_INPUT_VARIANTS.size;

export interface AtomsInputVariantsProps {
  /**
   * Input size.
   * - `"xs"` — Extra small for compact UIs
   * - `"sm"` — Small for secondary fields
   * - `"base"` — Default size
   * - `"lg"` — Large for prominent fields
   * @default "base"
   */
  size?: AtomsInputSize;
  /**
   * Whether the input is in an error state. Applies error ring styling.
   * @default false
   */
  error?: boolean;
  parentFocusIndicator?: boolean;
  focusIndicator?: boolean;
}

// Omit native `size` (conflicts with our `size` variant) and `required` (intentionally not part of the API).
type BaseInputProps = StripUndesiredProps<
  Omit<
    ComponentPropsWithoutRef<typeof BaseInput>,
    'size' | 'required' | 'aria-label' | 'aria-labelledby'
  >
> &
  AccessibleNameProps;

export function inputVariants({
  size = ATOMS_INPUT_DEFAULT_VARIANTS.size,
  error = false,
  parentFocusIndicator = false,
  focusIndicator = false,
}: AtomsInputVariantsProps = {}) {
  const errorRing = '!ring-od-border-error focus:ring-od-border-error focus:ring-2';
  return cn(
    // Base styles
    'border-0 bg-od-bg-primary text-od-content-primary ring ring-od-border-primary outline-none focus:outline-none',
    // Disabled state and placeholder styles (using vanilla CSS class for Chrome compatibility).
    // Matches Bricks Next: the ring recedes to the inactive border token so a disabled
    // field is visibly distinct from an enabled one (not just a near-identical background).
    'atoms-input-placeholder disabled:cursor-default disabled:text-od-content-inactive-primary disabled:bg-od-bg-inactive-secondary disabled:ring-od-border-inactive-primary',
    // Apply size styles from ATOMS_INPUT_VARIANTS
    ATOMS_INPUT_VARIANTS.size[size].classes,
    // Apply error styles when error is true
    error && errorRing,
    // Focus state handling
    parentFocusIndicator &&
      (error
        ? 'focus-within:ring-od-border-error focus-within:ring-2'
        : 'focus-within:ring-od-border-accent-brand focus-within:ring-2'),
    focusIndicator &&
      (error
        ? 'focus:ring-od-border-error focus:ring-2'
        : 'focus:ring-od-border-accent-brand focus:ring-2'),
  );
}

// ---------------------------------------------------------------------------
// Prop types — discriminated on `type`.
// ---------------------------------------------------------------------------

/** Free-text input types. Numeric entry uses `number`/`currency`/`percent`. */
type TextInputType = 'text' | 'email' | 'password' | 'search' | 'tel' | 'url';

/** Props for the text variants (the default). Value is a string. */
export type TextInputProps = Pick<AtomsInputVariantsProps, 'size' | 'error'> &
  Omit<BaseInputProps, 'type'> &
  AnalyticsProps & {
    /**
     * Text input type. For numeric entry use `type="number"`, `"currency"`, or
     * `"percent"`, which render a locale-formatted number field instead.
     * @default "text"
     */
    type?: TextInputType;
  };

/** Shared props for the numeric variants. Value is a number (or `null`). */
type NumericInputProps = Pick<AtomsInputVariantsProps, 'size' | 'error'> &
  AnalyticsProps &
  AccessibleNameProps & {
    /** Current value. `null` when the field is empty. */
    value?: number | null;
    /** Uncontrolled initial value. */
    defaultValue?: number;
    /** Fires on every change with the parsed number (`null` when empty). */
    onValueChange?: (value: number | null) => void;
    /** Fires when the value is committed (blur / Enter / arrow keys). */
    onValueCommitted?: (value: number | null) => void;
    /** Maximum number of digits after the decimal point. */
    decimals?: number;
    /** Minimum allowed value (the field clamps to it). */
    min?: number;
    /** Maximum allowed value (the field clamps to it). */
    max?: number;
    /** Increment/decrement amount for the arrow keys. @default 1 */
    step?: number;
    disabled?: boolean;
    readOnly?: boolean;
    /** Name of the hidden form input. */
    name?: string;
    placeholder?: string;
    className?: string;
    /**
     * Fires when focus leaves the input. Wire this to a `Field`/`Form` blur
     * handler to drive touched/blur-based validation.
     */
    onBlur?: FocusEventHandler<HTMLInputElement>;
    /** Fires when the input gains focus. */
    onFocus?: FocusEventHandler<HTMLInputElement>;
    /**
     * IDs of elements that describe the input (e.g. a `Field` description or
     * error message), associated for assistive tech.
     */
    'aria-describedby'?: string;
    /** Marks the input invalid for assistive tech (independent of `error` styling). */
    'aria-invalid'?: AriaAttributes['aria-invalid'];
    'aria-required'?: AriaAttributes['aria-required'];
  };

/**
 * Plain number entry.
 *
 * @public
 */
export type NumberInputProps = NumericInputProps & {
  type: 'number';
  /** Intl measurement unit, displayed after the number in its long form (e.g. "day"). */
  unit?: string;
};

/**
 * Percentage entry. The value is a fraction (Intl convention): `0.5` displays as
 * `50%`, and typing `50%` reports `0.5`.
 *
 * @public
 */
export type PercentInputProps = NumericInputProps & { type: 'percent' };

/**
 * Currency entry.
 *
 * @public
 */
export type CurrencyInputProps = NumericInputProps & {
  type: 'currency';
  /** ISO 4217 currency code. @default "USD" */
  currency?: string;
};

type NumericVariantProps = NumberInputProps | PercentInputProps | CurrencyInputProps;

/**
 * Input component props with accessibility guidance.
 *
 * **Accessible Name Required:** Input should have one of:
 * 1. `aria-label` — for bare inputs with visual placeholder
 * 2. `aria-labelledby` — for custom label association
 *
 * `type` discriminates the API:
 * - text types (`text`/`email`/…) → string `value`, all native input attributes.
 * - `number`/`currency`/`percent` → numeric `value` (a real number) backed by
 *   Base UI's NumberField, with `decimals`, `min`, `max`, `step`, and (for
 *   currency) `currency`.
 *
 * Missing accessible names will trigger console warnings in development.
 *
 * @example
 * <Input aria-label="Search products" placeholder="Search..." />
 * @example
 * <Input type="currency" aria-label="Price" value={price} onValueChange={setPrice} />
 * @example
 * <Input type="number" aria-label="Quantity" decimals={0} min={0} value={qty} onValueChange={setQty} />
 */
export type InputProps = TextInputProps | NumericVariantProps;

/**
 * Single-line text field built on Base UI's `Input`. Use for collecting short
 * free-form values (search, email, names). Sizes are tuned to the OD type ramp
 * via the `size` prop and an `error` flag toggles error-ring styling.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    className,
    size = 'base',
    error = false,
    analyticsName,
    trackImpression,
    ...inputProps
  } = props;

  // Inputs emit view events only — never change/value events. Typed input
  // values are PII and must not be sent to telemetry.
  const viewRef = useImpressionRef(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'inputViewEvent',
    ref,
  );

  useAccessibleNameWarning('Input', hasAccessibleName(props));

  return (
    <BaseInput
      ref={viewRef}
      className={cn(inputVariants({ size, error, focusIndicator: true }), className)}
      {...inputProps}
    />
  );
});

TextInput.displayName = 'Input';

/**
 * Numeric field built on Base UI's `NumberField`. Parses and formats with
 * `Intl.NumberFormat`, so grouping separators, currency symbols, and decimals
 * are handled correctly (and locale-aware), the reported value is a real number,
 * and caret/keyboard behavior comes for free. No stepper buttons are rendered.
 */
const NumberInput = forwardRef<HTMLInputElement, NumericVariantProps>((props, ref) => {
  const {
    size = 'base',
    error = false,
    analyticsName,
    trackImpression,
    className,
    value,
    defaultValue,
    onValueChange,
    onValueCommitted,
    decimals,
    min,
    max,
    step,
    disabled,
    readOnly,
    name,
    placeholder,
    id,
    onBlur,
    onFocus,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
  } = props;

  const currency = props.type === 'currency' ? (props.currency ?? 'USD') : undefined;
  const unit = props.type === 'number' ? props.unit : undefined;

  const format = useMemo<Intl.NumberFormatOptions>(() => {
    if (props.type === 'currency') {
      // Currency shows trailing zeros, so pin both bounds to `decimals`.
      return {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals ?? 2,
        maximumFractionDigits: decimals ?? 2,
      };
    }
    if (props.type === 'percent') {
      return { style: 'percent', maximumFractionDigits: decimals };
    }
    if (unit != null) {
      return { style: 'unit', unit, unitDisplay: 'long', maximumFractionDigits: decimals };
    }
    return { maximumFractionDigits: decimals };
  }, [props.type, currency, unit, decimals]);

  const viewRef = useImpressionRef(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'inputViewEvent',
    ref,
  );

  useAccessibleNameWarning('Input', Boolean(ariaLabel || ariaLabelledby || id));

  return (
    <NumberField.Root
      id={id}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      onValueCommitted={onValueCommitted}
      min={min}
      max={max}
      step={step}
      format={format}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
    >
      <NumberField.Input
        ref={viewRef}
        className={cn(inputVariants({ size, error, focusIndicator: true }), className)}
        placeholder={placeholder}
        onBlur={onBlur}
        onFocus={onFocus}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
      />
    </NumberField.Root>
  );
});

NumberInput.displayName = 'Input';

/**
 * Single-line input. Renders a free-text field by default, or a locale-formatted
 * numeric field when `type` is `"number"`, `"currency"`, or `"percent"`.
 *
 * Provide an accessible name with `aria-label` or `aria-labelledby` — a missing
 * name logs a console warning in development.
 *
 * @example
 * ```tsx
 * <Input aria-label="Search products" placeholder="Search..." />
 * <Input aria-label="Email" size="sm" type="email" />
 * <Input type="currency" aria-label="Price" value={price} onValueChange={setPrice} />
 * <Input type="percent" aria-label="Rate" value={rate} onValueChange={setRate} />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  if (props.type === 'number' || props.type === 'currency' || props.type === 'percent') {
    return <NumberInput ref={ref} {...props} />;
  }
  return <TextInput ref={ref} {...props} />;
});

Input.displayName = 'Input';
